import React, { useState } from 'react';
import { 
  FaArrowLeft, FaLock, FaThumbtack, FaUser, FaEye, FaCalendar, 
  FaReply, FaPaperPlane
} from 'react-icons/fa';
import GeminiChat from '../GeminiChat';
import './Discussion.css';

const Discussion = ({ discussion, onCreateReply, onBack, currentUser }) => {
  const [replyContent, setReplyContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    
    if (!replyContent.trim() || isSubmitting) return;
    if (!currentUser) {
      console.error('Utilisateur non connecté');
      return;
    }

    setIsSubmitting(true);
    try {
      await onCreateReply(discussion.id, replyContent);
      setReplyContent('');
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la réponse:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fonction pour utiliser la réponse de l'IA
  const handleUseAIResponse = (aiResponse) => {
    setReplyContent(aiResponse);
  };

  return (
    <div className="discussion-with-chat">
      {/* Contenu principal de la discussion */}
      <div className="discussion-main">
        <div className="discussion-detail">
          {/* En-tête de la discussion */}
          <div className="discussion-header">
            <button onClick={onBack} className="back-button">
              <FaArrowLeft className="icon" />
              Retour à la liste
            </button>
            
            <div className="discussion-title-section">
              <h1>{discussion.title}</h1>
            </div>
            
            <div className="discussion-meta">
              <span className="category">
                {discussion.category?.name}
              </span>
              <span className="author">
                <FaUser className="icon" />
                Par {discussion.author?.name || 'Utilisateur'}
              </span>
              <span className="date">
                <FaCalendar className="icon" />
                {new Date(discussion.created_at).toLocaleDateString()}
              </span>
              <span className="views">
                <FaEye className="icon" />
                {discussion.views || 0} vues
              </span>
            </div>
            
            <div className="badges">
              {discussion.is_pinned && (
                <span className="badge pinned">
                  <FaThumbtack className="icon" />
                  Épinglé
                </span>
              )}
              {discussion.is_locked && (
                <span className="badge locked">
                  <FaLock className="icon" />
                  Verrouillé
                </span>
              )}
            </div>
          </div>

          {/* Contenu principal */}
          <div className="discussion-content">
            <div className="original-post">
              <div className="post-header">
                <div className="user-info">
                  <div className="user-avatar">
                    {discussion.author?.name?.charAt(0) || 'U'}
                  </div>
                  <div className="user-details">
                    <div className="user-name">{discussion.author?.name || 'Utilisateur'}</div>
                    <div className="post-date">
                      <FaCalendar className="icon" />
                      {new Date(discussion.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="post-content">
                {discussion.content}
              </div>
            </div>

            {/* Réponses */}
            <div className="replies-section">
              <h3>
                <FaReply className="icon" />
                Réponses ({discussion.replies?.length || discussion.replies_count || 0})
              </h3>
              
              {discussion.replies && discussion.replies.length > 0 ? (
                <div className="replies-list">
                  {discussion.replies.map((reply, index) => (
                    <div key={reply.id || index} className={`reply ${reply.is_ai_generated ? 'ai-generated' : ''}`}>
                      <div className="reply-header">
                        <div className="user-info">
                          <div className="user-avatar small">
                            {reply.author?.name?.charAt(0) || 'U'}
                          </div>
                          <div className="user-details">
                            <div className="user-name">
                              {reply.author?.name || 'Utilisateur'}
                              {reply.is_ai_generated && (
                                <span className="ai-badge">
                                  🤖 Assisté par IA
                                </span>
                              )}
                            </div>
                            <div className="post-date">
                              <FaCalendar className="icon" />
                              {new Date(reply.created_at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="reply-content">
                        {reply.content}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-replies">
                  <FaReply className="icon" />
                  Aucune réponse pour le moment. Soyez le premier à répondre !
                </div>
              )}
            </div>

            {/* Formulaire de réponse */}
            {!discussion.is_locked && currentUser ? (
              <form onSubmit={handleSubmitReply} className="reply-form">
                <div className="reply-form-header">
                  <h4>
                    <FaReply className="icon" />
                    Ajouter une réponse
                  </h4>
                </div>
                
                <textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="Votre réponse... (Utilisez l'assistant IA à droite pour vous aider)"
                  rows="6"
                  required
                  maxLength="5000"
                  className="reply-textarea"
                />
                
                <div className="form-actions">
                  <div className="form-info">
                    <div className="char-counter">
                      {replyContent.length}/5000 caractères
                    </div>
                    {replyContent.length > 0 && (
                      <div className="word-count">
                        {replyContent.split(/\s+/).filter(word => word.length > 0).length} mots
                      </div>
                    )}
                  </div>
                  <button 
                    type="submit" 
                    disabled={isSubmitting || !replyContent.trim()}
                    className="submit-button"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="loading-spinner"></div>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <FaPaperPlane className="icon" />
                        Poster la réponse
                      </>
                    )}
                  </button>
                </div>
              </form>
            ) : !discussion.is_locked && !currentUser ? (
              <div className="auth-required-message">
                <FaLock className="icon" />
                <a href="/connexion">Connectez-vous</a> pour répondre à cette discussion.
              </div>
            ) : null}

            {discussion.is_locked && (
              <div className="locked-message">
                <FaLock className="icon" />
                Cette discussion est verrouillée. Vous ne pouvez plus y répondre.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sidebar IA toujours visible */}
      <GeminiChat 
        onUseResponse={handleUseAIResponse}
        context={`Discussion: "${discussion.title}"\n\nContenu: "${discussion.content}"\n\nJe cherche de l'aide pour formuler une réponse appropriée à cette discussion.`}
      />
    </div>
  );
};

export default Discussion;